import { useSecureGraphqlQuery } from '@psyomics/ui-common';
import { Header, LoadingSpinner, PageLayout, Text } from '@psyomics/ui-components';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetAllAvailableContentDocument, GetAllAvailableContentQuery } from '../generated/content';

import { ContentItem } from './content-item';
import { TopicFilters } from './topic-filters';
import { TopicType, type ContentItemType } from './types';

type TopicGroup = { title: string; items: ContentItemType[] };
type TopicGroupEntry = readonly [topicCode: string, group: TopicGroup];

const OTHER_TOPIC_CODE = 'other';

const compareTopicGroupEntries = ([topicCodeA, groupA]: TopicGroupEntry, [topicCodeB, groupB]: TopicGroupEntry) => {
  return compareTopics({ code: topicCodeA, title: groupA.title }, { code: topicCodeB, title: groupB.title });
};

const compareTopics = ({ code: codeA, title: titleA }: TopicType, { code: codeB, title: titleB }: TopicType) => {
  if (codeA === OTHER_TOPIC_CODE && codeB !== OTHER_TOPIC_CODE) return 1;
  if (codeA !== OTHER_TOPIC_CODE && codeB === OTHER_TOPIC_CODE) return -1;
  return titleA.localeCompare(titleB);
};

const buildRowsByTopic = (rows: ContentItemType[], selectedTopicCodes: string[]) =>
  rows.reduce<Record<string, TopicGroup>>((acc, item) => {
    const topics = item.topics.filter(
      (topic) => selectedTopicCodes.length === 0 || selectedTopicCodes.includes(topic.code)
    );

    for (const topic of topics) {
      if (!acc[topic.code]) acc[topic.code] = { items: [], title: topic.title };
      acc[topic.code].items.push(item);
    }

    return acc;
  }, {});

const useContentIndexData = (otherTopic: TopicType) => {
  const [{ data, fetching }] = useSecureGraphqlQuery<GetAllAvailableContentQuery>({
    query: GetAllAvailableContentDocument,
  });
  const rows = useMemo(
    () =>
      (data?.getAllAvailableContent ?? []).map(({ topics, ...rest }) => ({
        ...rest,
        topics: topics.length ? topics : [otherTopic],
      })),
    [data, otherTopic]
  );
  const allTopics = useMemo(
    () =>
      Array.from(
        new Map(
          rows
            .flatMap((item) => item.topics)
            .map((topic) => [topic.code, { code: topic.code, title: topic.title }] as const)
        ).values()
      ).sort(compareTopics),
    [rows]
  );
  const [selectedTopicCodes, setSelectedTopicCodes] = useState<string[]>(allTopics.map(({ code }) => code));

  const toggleTopic = (topicCode: string) => {
    setSelectedTopicCodes((current) =>
      current.includes(topicCode) ? current.filter((selected) => selected !== topicCode) : [...current, topicCode]
    );
  };

  const topicGroups = useMemo(() => {
    const filteredRows = rows.filter((item) => item.topics.some((topic) => selectedTopicCodes.includes(topic.code)));

    const rowsByTopic = buildRowsByTopic(filteredRows, selectedTopicCodes);
    return selectedTopicCodes
      .map((topicCode) => [topicCode, rowsByTopic[topicCode]] as const)
      .filter(([, group]) => Boolean(group))
      .sort(compareTopicGroupEntries);
  }, [rows, selectedTopicCodes]);

  return { allTopics, fetching, selectedTopicCodes, toggleTopic, topicGroups };
};

const ContentIndex = () => {
  const { t } = useTranslation();
  const otherTopic = useMemo(() => ({ code: OTHER_TOPIC_CODE, title: t('page.healthLibrary.otherTopicTitle') }), [t]);
  const { allTopics, fetching, selectedTopicCodes, toggleTopic, topicGroups } = useContentIndexData(otherTopic);

  if (fetching) return <LoadingSpinner />;

  return (
    <PageLayout title={t('page.healthLibrary.title')}>
      {['subtitle', 'subtitle2', 'subtitle3'].map((subtitle) => (
        <Text className="mb-2" key={subtitle}>
          {t(`page.healthLibrary.${subtitle}`)}
        </Text>
      ))}
      <TopicFilters allTopics={allTopics} selectedTopicCodes={selectedTopicCodes} toggleTopic={toggleTopic} />
      {topicGroups.map(([topicCode, group]) => (
        <section className="mt-8 min-w-0" key={topicCode}>
          <Header level="h2">{group.title}</Header>
          <div className="no-scrollbar mt-2 min-w-0 overflow-x-auto pb-2">
            <ul className="flex w-max gap-4">
              {group.items.map((item) => (
                <ContentItem item={item} key={`${topicCode}-${item.id}`} />
              ))}
            </ul>
          </div>
        </section>
      ))}
    </PageLayout>
  );
};

export { compareTopicGroupEntries, compareTopics, ContentIndex, type TopicGroupEntry };
